import { useState } from "react";

/**
 * Full carousel unit: iPhone frame with sliding video/text panes,
 * dot toggle at the bottom of the phone, and the current step's
 * title + subtitle rendered below the phone frame.
 *
 * Panes slide horizontally (left) when the active step advances.
 */
export const PhoneCarousel = ({ label, steps, testId = "phone-carousel" }) => {
  const [active, setActive] = useState(0);
  const step = steps[active];

  return (
    <div data-testid={testId} className="flex flex-col items-center">
      {/* Phone frame */}
      <div
        className="phone-frame relative w-[300px] sm:w-[320px] md:w-[340px] aspect-[9/19.2] rounded-[3rem] p-[7px] bg-gradient-to-b from-[#1a1a1a] via-[#0a0a0a] to-[#0e0e0e] border border-[#1f1f1f]"
      >
        <span className="absolute left-[-2px] top-[110px] w-[2px] h-10 rounded-r bg-[#1c1c1c]" />
        <span className="absolute left-[-2px] top-[160px] w-[2px] h-16 rounded-r bg-[#1c1c1c]" />
        <span className="absolute right-[-2px] top-[140px] w-[2px] h-20 rounded-l bg-[#1c1c1c]" />

        <div className="relative w-full h-full rounded-[2.55rem] overflow-hidden bg-black">
          {/* Sliding strip */}
          <div
            className="absolute inset-0 flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${active * 100}%)` }}
          >
            {steps.map((s, i) => (
              <div
                key={i}
                className="relative w-full h-full flex-shrink-0"
              >
                {s.video ? (
                  <video
                    src={s.video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-contain object-center bg-black"
                    draggable={false}
                  />
                ) : s.image ? (
                  <img
                    src={s.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain object-top bg-black"
                    draggable={false}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col justify-center px-6 text-center">
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 w-[88px] h-[26px] rounded-full bg-black/95" />
                    <span className="verse-cite brand-green mb-4">{label}</span>
                    <div className="font-display text-lg leading-[1.35] text-rooted">
                      {s.title}
                    </div>
                    <div className="mt-3 font-body text-[12px] leading-[1.6] text-muted-rooted">
                      {s.subtitle}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Dot toggle at bottom of phone */}
          <div className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-2.5 z-30">
            {steps.map((_, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Show step ${i + 1}`}
                  data-testid={`${testId}-dot-${i + 1}`}
                  onClick={() => setActive(i)}
                  className={`h-2.5 rounded-full transition-all duration-500 ${
                    isActive ? "w-6 bg-[#39971a]" : "w-2.5 bg-white/25 hover:bg-white/45"
                  }`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Step title + subtitle — outside/below the phone */}
      <div
        data-testid={`${testId}-caption`}
        className="mt-8 text-center max-w-sm px-2 min-h-[70px]"
      >
        <div
          key={`t-${active}`}
          className="font-display text-lg md:text-xl text-rooted rise"
        >
          {step?.title}
        </div>
        <div
          key={`s-${active}`}
          className="mt-2 font-body text-sm md:text-base text-muted-rooted leading-[1.6] rise-delay-1"
        >
          {step?.subtitle}
        </div>
      </div>
    </div>
  );
};

export default PhoneCarousel;
